import React, { useState } from 'react';
import { ActivityCalendar } from 'react-activity-calendar';
import { 
  FaGithub, 
  FaCheckCircle, 
  FaTerminal, 
  FaFire, 
  FaCube, 
  FaExternalLinkAlt, 
  FaSearch,
  FaSlidersH
} from 'react-icons/fa';

// Mock Data representing your verified ledger state
const MOCK_PROFILE = {
  name: "Kandarp Dave",
  handle: "kdave",
  bio: "Building the future of verified proof-of-work. Technical Architect & Solo Founder.",
  location: "Las Vegas, NV",
  streak: 14,
  totalCommits: 142,
  verifiedRate: "94.2%"
};

const MOCK_SKILLS = [
  { name: "Knex.js", type: "Tool", level: "Expert", logs: 24, verified: true },
  { name: "PostgreSQL", type: "Domain", level: "Advanced", logs: 38, verified: true },
  { name: "LLM Structured Extraction", type: "Skill", level: "Expert", logs: 19, verified: true },
  { name: "AWS Cloud Architecture", type: "Domain", level: "Intermediate", logs: 12, verified: true },
  { name: "Rapid Execution", type: "Soft Skill", level: "Foundational", logs: 49, verified: false }
];

const MOCK_FEED = [
  {
    id: "log_01",
    timestamp: "2 hours ago",
    task: "Optimized PostgreSQL vector embedding pipeline using custom batching chunks, cutting operational latency by 64%.",
    domain: "Backend Systems",
    quizPassed: true,
    quizContext: "Tested on batch transaction boundaries & index utilization values."
  },
  {
    id: "log_02",
    timestamp: "Yesterday",
    task: "Refactored Knex.js table migrations to isolate user_missing_skill collections and streamline flexible taxonomy mapping.",
    domain: "Database Architecture",
    quizPassed: true,
    quizContext: "Tested on foreign key cascading constraints & dynamic schemas."
  },
  {
    id: "log_03",
    timestamp: "3 days ago",
    task: "Configured automated job-crawling microservice worker to scrape raw target roles into structured JSON schema configurations.",
    domain: "Data Pipelines",
    quizPassed: true,
    quizContext: "Tested on handling unexpected token arrays & error boundaries."
  }
];

// Sample data structured specifically for the react-activity-calendar format
const MOCK_CALENDAR_DATA = [
  { date: '2026-05-01', count: 2, level: 1 },
  { date: '2026-05-02', count: 4, level: 3 },
  { date: '2026-05-03', count: 1, level: 1 },
  { date: '2026-05-04', count: 0, level: 0 },
  { date: '2026-05-05', count: 6, level: 4 },
  { date: '2026-05-06', count: 3, level: 2 },
  { date: '2026-05-07', count: 4, level: 3 },
  { date: '2026-05-10', count: 2, level: 1 },
  { date: '2026-05-11', count: 5, level: 4 },
  { date: '2026-05-12', count: 3, level: 2 },
  { date: '2026-05-13', count: 8, level: 4 },
  { date: '2026-05-14', count: 1, level: 1 },
  { date: '2026-05-18', count: 4, level: 2 },
  { date: '2026-05-19', count: 5, level: 3 },
  { date: '2026-05-20', count: 3, level: 2 },
  { date: '2026-05-21', count: 6, level: 4 },
  { date: '2026-05-22', count: 4, level: 3 },
];

export default function LiveResumeHub() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div style={styles.container}>
      {/* Top GitHub-style Navigation Bar */}
      <header style={styles.navBar}>
        <div style={styles.navLeft}>
          <FaGithub style={styles.logoIcon} />
          <span style={styles.logoText}>{MOCK_PROFILE.handle}</span>
          <span style={styles.divider}>/</span>
          <span style={styles.repoName}>live-resume</span>
          <span style={styles.badge}>Public Ledger</span>
        </div>
        <div style={styles.navRight}>
          <div style={styles.searchBox}>
            <FaSearch style={styles.searchIcon} />
            <input type="text" placeholder="Search skills, logs, jobs..." style={styles.searchInput} />
          </div>
        </div>
      </header>

      {/* Main Profile Layout split into Sidebar and Main content */}
      <div style={styles.layoutGrid}>
        
        {/* Left Side Column: Profile Identity */}
        <aside style={styles.sidebar}>
          <div style={styles.avatarContainer}>
            <div style={styles.avatarPlaceholder}>
              {MOCK_PROFILE.name.split(' ').map(n => n[0]).join('')}
            </div>
          </div>
          
          <h1 style={styles.profileName}>{MOCK_PROFILE.name}</h1>
          <h2 style={styles.profileHandle}>@{MOCK_PROFILE.handle}</h2>
          <p style={styles.profileBio}>{MOCK_PROFILE.bio}</p>
          
          <div style={styles.metaList}>
            <div style={styles.metaItem}>{MOCK_PROFILE.location}</div>
          </div>

          <hr style={styles.sectionDivider} />

          {/* Quick Metrics Panel */}
          <div style={styles.metricsCard}>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}><FaFire style={{ color: '#ff6b6b' }} /> Day Streak</span>
              <span style={styles.metricValue}>{MOCK_PROFILE.streak}</span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}><FaTerminal /> Total Logs</span>
              <span style={styles.metricValue}>{MOCK_PROFILE.totalCommits}</span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}><FaCheckCircle style={{ color: '#2ecc71' }} /> Verification Rate</span>
              <span style={styles.metricValue}>{MOCK_PROFILE.verifiedRate}</span>
            </div>
          </div>
        </aside>

        {/* Right Side Column: Dynamic Tabs and Dense Proof Data */}
        <main style={styles.mainContent}>
          {/* Sub-tab Navigation */}
          <nav style={styles.tabNav}>
            <button 
              onClick={() => setActiveTab('overview')} 
              style={{ ...styles.tabButton, ...(activeTab === 'overview' ? styles.activeTabButton : {}) }}
            >
              <FaCube style={{ marginRight: '6px' }} /> Overview
            </button>
            <button 
              onClick={() => setActiveTab('skills')} 
              style={{ ...styles.tabButton, ...(activeTab === 'skills' ? styles.activeTabButton : {}) }}
            >
              <FaSlidersH style={{ marginRight: '6px' }} /> Skill Graph
            </button>
          </nav>

          {activeTab === 'overview' && (
            <>
              {/* section: Activity Contribution Calendar */}
              <section style={styles.contentSection}>
                <h3 style={styles.sectionHeader}>Proof-of-Work Contributions</h3>
                <div style={styles.calendarWrapper}>
                  <ActivityCalendar 
                    data={MOCK_CALENDAR_DATA} 
                    theme={{
                      dark: ['#161b22', '#0e4429', '#006d32', '#26a641', '#39d353']
                    }}
                    labels={{
                      totalCount: '{{count}} verification activities in the last month'
                    }}
                    hideColorLegend
                    blockRadius={2}
                    blockSize={12}
                    blockMargin={4}
                  />
                </div>
              </section>

              {/* Section: Top Verified Skills Moat */}
              <section style={styles.contentSection}>
                <h3 style={styles.sectionHeader}>Verified Competency Moat</h3>
                <div style={styles.skillsGrid}>
                  {MOCK_SKILLS.map((skill, index) => (
                    <div key={index} style={styles.skillCard}>
                      <div style={styles.skillHeader}>
                        <span style={styles.skillName}>{skill.name}</span>
                        {skill.verified && <FaCheckCircle style={styles.verifiedIcon} title="Verified via Micro-Quiz" />}
                      </div>
                      <div style={styles.skillMeta}>
                        <span style={styles.skillTypeTag}>{skill.type}</span>
                        <span style={styles.skillLevel}>{skill.level}</span>
                      </div>
                      <div style={styles.skillCountRow}>
                        <span style={styles.skillCountText}>{skill.logs} direct check-ins</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section: Activity / Commit Log Stream */}
              <section style={styles.contentSection}>
                <h3 style={styles.sectionHeader}>Recent Check-In Activity</h3>
                <div style={styles.timeline}>
                  {MOCK_FEED.map((log) => (
                    <div key={log.id} style={styles.timelineItem}>
                      <div style={styles.timelineIndicator}>
                        <div style={styles.timelineLine}></div>
                        <div style={styles.timelineNode}></div>
                      </div>
                      <div style={styles.timelineContentCard}>
                        <div style={styles.timelineHeader}>
                          <span style={styles.logDomain}>{log.domain}</span>
                          <span style={styles.logTime}>{log.timestamp}</span>
                        </div>
                        <p style={styles.logTaskText}>{log.task}</p>
                        {log.quizPassed && (
                          <div style={styles.quizValidationBanner}>
                            <FaCheckCircle style={styles.innerVerifiedIcon} />
                            <span style={styles.quizContextText}>{log.quizContext}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {activeTab === 'skills' && (
            <section style={styles.contentSection}>
              <h3 style={styles.sectionHeader}>Comprehensive Ledger Overview</h3>
              <p style={styles.emptyText}>Skill graph visualization container. Deep-dive sub-views map cleanly to individual table schemas.</p>
            </section>
          )}

        </main>
      </div>
    </div>
  );
}

// Scannable, clean styles optimized for dark theme environments
const styles = {
  container: {
    backgroundColor: '#0d1117',
    color: '#c9d1d9',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    paddingBottom: '40px'
  },
  navBar: {
    backgroundColor: '#161b22',
    borderBottom: '1px solid #30363d',
    padding: '16px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  logoIcon: {
    fontSize: '24px',
    color: '#f0f6fc'
  },
  logoText: {
    fontWeight: '600',
    color: '#f0f6fc'
  },
  divider: {
    color: '#8b949e'
  },
  repoName: {
    fontWeight: '600',
    color: '#f0f6fc'
  },
  badge: {
    border: '1px solid #30363d',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '12px',
    color: '#8b949e',
    backgroundColor: '#0d1117',
    marginLeft: '8px'
  },
  navRight: {
    display: 'flex',
    alignItems: 'center'
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    color: '#8b949e',
    fontSize: '14px'
  },
  searchInput: {
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '5px 12px 5px 32px',
    color: '#c9d1d9',
    fontSize: '14px',
    width: '240px',
    outline: 'none'
  },
  layoutGrid: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '32px',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '32px 24px'
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column'
  },
  avatarContainer: {
    marginBottom: '16px'
  },
  avatarPlaceholder: {
    width: '280px',
    height: '280px',
    borderRadius: '50%',
    backgroundColor: '#1f242c',
    border: '1px solid #30363d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '64px',
    fontWeight: 'bold',
    color: '#f0f6fc'
  },
  profileName: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#f0f6fc',
    margin: '0 0 4px 0'
  },
  profileHandle: {
    fontSize: '20px',
    fontWeight: '300',
    color: '#8b949e',
    margin: '0 0 16px 0'
  },
  profileBio: {
    fontSize: '14px',
    lineHeight: '1.5',
    margin: '0 0 16px 0',
    color: '#c9d1d9'
  },
  metaList: {
    fontSize: '14px',
    color: '#8b949e',
    marginBottom: '16px'
  },
  sectionDivider: {
    border: '0',
    borderTop: '1px solid #21262d',
    margin: '16px 0'
  },
  metricsCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px'
  },
  metricLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#8b949e'
  },
  metricValue: {
    fontWeight: '600',
    color: '#f0f6fc'
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  tabNav: {
    display: 'flex',
    gap: '8px',
    borderBottom: '1px solid #30363d',
    paddingBottom: '8px'
  },
  tabButton: {
    background: 'none',
    border: 'none',
    color: '#c9d1d9',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px'
  },
  activeTabButton: {
    backgroundColor: '#21262d',
    fontWeight: '600',
    borderBottom: '2px solid #f78166',
    borderRadius: '6px 6px 0 0'
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  sectionHeader: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0',
    color: '#f0f6fc'
  },
  calendarWrapper: {
    border: '1px solid #30363d',
    backgroundColor: '#161b22',
    borderRadius: '6px',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center'
  },
  skillsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '12px'
  },
  skillCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  skillHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  skillName: {
    fontWeight: '600',
    color: '#58a6ff',
    fontSize: '14px'
  },
  verifiedIcon: {
    color: '#2ecc71',
    fontSize: '14px'
  },
  skillMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px'
  },
  skillTypeTag: {
    backgroundColor: '#21262d',
    padding: '2px 6px',
    borderRadius: '4px',
    color: '#8b949e',
    border: '1px solid #30363d'
  },
  skillLevel: {
    color: '#8b949e'
  },
  skillCountRow: {
    borderTop: '1px solid #21262d',
    paddingTop: '8px',
    marginTop: '4px'
  },
  skillCountText: {
    fontSize: '12px',
    color: '#8b949e'
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column'
  },
  timelineItem: {
    display: 'flex',
    gap: '16px'
  },
  timelineIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative'
  },
  timelineLine: {
    width: '2px',
    backgroundColor: '#30363d',
    flexGrow: 1,
    position: 'absolute',
    top: '24px',
    bottom: '0'
  },
  timelineNode: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#21262d',
    border: '2px solid #30363d',
    marginTop: '6px',
    zIndex: 1
  },
  timelineContentCard: {
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '16px',
    flexGrow: 1,
    marginBottom: '16px'
  },
  timelineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#8b949e',
    marginBottom: '8px'
  },
  logDomain: {
    fontWeight: '600',
    color: '#c9d1d9'
  },
  logTime: {
    fontSize: '11px'
  },
  logTaskText: {
    fontSize: '14px',
    lineHeight: '1.4',
    margin: '0 0 12px 0',
    color: '#c9d1d9'
  },
  quizValidationBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(46, 204, 113, 0.1)',
    border: '1px solid rgba(46, 204, 113, 0.2)',
    borderRadius: '4px',
    padding: '8px 12px'
  },
  innerVerifiedIcon: {
    color: '#2ecc71',
    fontSize: '14px',
    flexShrink: 0
  },
  quizContextText: {
    fontSize: '12px',
    color: '#a3ebd0'
  },
  emptyText: {
    color: '#8b949e',
    fontSize: '14px',
    fontStyle: 'italic'
  }
};